import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styles from './Popover.module.scss';
import { getClassName } from '@onsolve-ucl/utils.get-class-name';
import { debounce } from 'lodash';
export var PopoverPosition;
(function (PopoverPosition) {
    PopoverPosition["BOTTOM"] = "bottom";
    PopoverPosition["BOTTOM_LEFT"] = "bottom left";
    PopoverPosition["BOTTOM_RIGHT"] = "bottom right";
    PopoverPosition["LEFT"] = "left";
    PopoverPosition["LEFT_BOTTOM"] = "left bottom";
    PopoverPosition["LEFT_TOP"] = "left top";
    PopoverPosition["RIGHT"] = "right";
    PopoverPosition["RIGHT_BOTTOM"] = "right bottom";
    PopoverPosition["RIGHT_TOP"] = "right top";
    PopoverPosition["TOP"] = "top";
    PopoverPosition["TOP_LEFT"] = "top left";
    PopoverPosition["TOP_RIGHT"] = "top right";
})(PopoverPosition || (PopoverPosition = {}));
const POPOVER_ROOT_ID = 'onsolve-popover-root';
const VIEWPORT_PADDING = 10;
// Check if the popoverRoot element exists, if not, create it
let popoverRoot = document.getElementById(POPOVER_ROOT_ID);
if (!popoverRoot) {
    popoverRoot = document.createElement('div');
    popoverRoot.id = POPOVER_ROOT_ID;
    document.body.appendChild(popoverRoot);
}
const isAuto = (value) => {
    return value === 'auto';
};
const numberToPixels = (value) => `${value}px`;
const getRefValues = (element) => {
    return element
        ? element.getBoundingClientRect()
        : {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: 0,
            height: 0,
        };
};
const updatePopoverPositionDebounced = debounce(({ initialHeight, initialPosition, initialWidth, popoverRef, popoverStyles, refElement, updateState }) => {
    let newPopoverStyles = {};
    const firstChild = popoverRef.firstElementChild;
    const { height: popoverHeight, width: popoverWidth } = getRefValues(popoverRef);
    const { bottom: refElemBottom, height: refElemHeight, left: refElemLeft, right: refElemRight, top: refElemTop, width: refElemWidth, } = getRefValues(refElement);
    const { height: firstChildHeight, width: firstChildWidth } = getRefValues(firstChild);
    const distanceToBottom = window.innerHeight - (refElemBottom + window.scrollY) - VIEWPORT_PADDING;
    const distanceToLeft = refElemLeft + window.scrollX - VIEWPORT_PADDING;
    const distanceToRight = window.innerWidth - (refElemRight + window.scrollX) - VIEWPORT_PADDING;
    const distanceToTop = refElemTop + window.scrollY - VIEWPORT_PADDING;
    const greatestVerticalDistance = Math.max(distanceToTop, distanceToBottom);
    const greatestHorizontalDistance = Math.max(distanceToLeft, distanceToRight);
    const getNewWidth = () => {
        let newWidth = popoverWidth;
        // Reset size to original if needed
        if (initialWidth === 'refWidth') {
            newWidth = refElemWidth;
        }
        else if (initialWidth === 'auto') {
            newWidth = firstChildWidth;
        }
        else if (Number.isInteger(initialWidth)) {
            newWidth = initialWidth;
        }
        // Reduce size if it's larger than the greatest distance
        if (newWidth > greatestHorizontalDistance) {
            newWidth =
                distanceToLeft > distanceToRight
                    ? distanceToLeft - VIEWPORT_PADDING
                    : distanceToRight - VIEWPORT_PADDING;
        }
        return newWidth;
    };
    const getNewHeight = () => {
        let newHeight = popoverHeight;
        // Reset Size to origin if needed
        if (initialHeight === 'refHeight') {
            newHeight = refElemHeight;
        }
        else if (initialHeight === 'auto') {
            newHeight = firstChildHeight;
        }
        else if (Number.isInteger(initialHeight)) {
            newHeight = initialHeight;
        }
        // Reduce size if it's larger than the greatest distance
        if (newHeight > greatestVerticalDistance) {
            newHeight =
                distanceToTop > distanceToBottom
                    ? distanceToTop - VIEWPORT_PADDING
                    : distanceToBottom - VIEWPORT_PADDING;
        }
        return newHeight;
    };
    const currentWidth = getNewWidth();
    const currentHeight = getNewHeight();
    const getNewTransformOrigin = (position) => {
        const checkPosition = {
            [PopoverPosition.BOTTOM]: () => currentHeight > distanceToBottom ? PopoverPosition.TOP : PopoverPosition.BOTTOM,
            [PopoverPosition.TOP]: () => currentHeight > distanceToTop ? PopoverPosition.BOTTOM : PopoverPosition.TOP,
            [PopoverPosition.LEFT]: () => currentWidth > distanceToRight ? PopoverPosition.RIGHT : PopoverPosition.LEFT,
            [PopoverPosition.RIGHT]: () => currentWidth > distanceToLeft ? PopoverPosition.LEFT : PopoverPosition.RIGHT,
        };
        return position
            .split(' ')
            .map((transformVal) => checkPosition[transformVal]())
            .join(' ');
    };
    const getLeftPosition = () => numberToPixels(refElemLeft + window.scrollX);
    const getRightPosition = () => numberToPixels(window.innerWidth - (refElemRight + window.scrollX));
    const getTopPosition = () => numberToPixels(refElemBottom + window.scrollY);
    const getBottomPosition = () => numberToPixels(window.innerHeight - (refElemTop + window.scrollY));
    const getLeftMiddlePosition = () => numberToPixels(refElemLeft + refElemWidth / 2 + window.scrollX);
    const getTopMiddlePosition = () => numberToPixels(refElemTop + refElemHeight / 2 + window.scrollY);
    const getPositionStyles = {
        [PopoverPosition.BOTTOM]: () => ({
            left: getLeftMiddlePosition(),
            top: getTopPosition(),
        }),
        [PopoverPosition.BOTTOM_LEFT]: () => ({
            left: getLeftPosition(),
            top: getTopPosition(),
        }),
        [PopoverPosition.BOTTOM_RIGHT]: () => ({
            right: getRightPosition(),
            top: getTopPosition(),
        }),
        [PopoverPosition.TOP]: () => ({
            left: getLeftMiddlePosition(),
            bottom: getBottomPosition(),
        }),
        [PopoverPosition.TOP_LEFT]: () => ({
            left: getLeftPosition(),
            bottom: getBottomPosition(),
        }),
        [PopoverPosition.TOP_RIGHT]: () => ({
            right: getRightPosition(),
            bottom: getBottomPosition(),
        }),
        [PopoverPosition.LEFT]: () => ({
            right: getRightPosition(),
            top: getTopMiddlePosition(),
        }),
        [PopoverPosition.LEFT_BOTTOM]: () => ({
            right: getRightPosition(),
            bottom: getBottomPosition(),
        }),
        [PopoverPosition.LEFT_TOP]: () => ({
            right: getRightPosition(),
            top: getTopPosition(),
        }),
        [PopoverPosition.RIGHT]: () => ({
            left: getLeftPosition(),
            top: getTopMiddlePosition(),
        }),
        [PopoverPosition.RIGHT_BOTTOM]: () => ({
            left: getLeftPosition(),
            bottom: getBottomPosition(),
        }),
        [PopoverPosition.RIGHT_TOP]: () => ({
            left: getLeftPosition(),
            top: getTopPosition(),
        }),
    };
    newPopoverStyles.transformOrigin = getNewTransformOrigin(initialPosition);
    newPopoverStyles.height =
        isAuto(initialHeight) && firstChildHeight < greatestVerticalDistance
            ? initialHeight
            : numberToPixels(currentHeight);
    newPopoverStyles.width =
        isAuto(initialWidth) && firstChildWidth < greatestHorizontalDistance
            ? initialWidth
            : numberToPixels(currentWidth);
    newPopoverStyles = { ...newPopoverStyles, ...getPositionStyles[popoverStyles.transformOrigin]() };
    if (Object.keys(newPopoverStyles).some((key) => newPopoverStyles[key] !== popoverStyles?.[key])) {
        updateState({ popoverStyles: newPopoverStyles, initialStylesSet: true });
    }
}, 100);
export const Popover = ({ children, className, closeOnOutsideClick = true, initialPosition = PopoverPosition.BOTTOM_LEFT, initialHeight = 'auto', initialWidth = 'auto', noScroll = false, onClose, open, refElement, }) => {
    const popoverRef = useRef(null);
    const [{ initialStylesSet, isMounted, popoverStyles }, setState] = useState({
        initialStylesSet: false,
        isMounted: false,
        popoverStyles: {
            bottom: 0,
            left: 0,
            height: 0,
            overflow: 'visible',
            right: 0,
            top: 0,
            transformOrigin: initialPosition,
            width: 0,
        },
    });
    const updateState = useCallback((newState) => {
        setState((prevState) => ({ ...prevState, ...newState }));
    }, []);
    const updatePopoverPosition = useCallback(() => {
        if (open && isMounted && refElement && popoverRef.current) {
            updatePopoverPositionDebounced({
                initialHeight,
                initialPosition,
                initialWidth,
                popoverRef: popoverRef.current,
                popoverStyles,
                refElement,
                updateState,
            });
        }
    }, [initialHeight, initialPosition, initialWidth, isMounted, open, popoverStyles, refElement, updateState]);
    // Update popover position and size on resize
    useEffect(() => {
        const popover = popoverRef.current;
        if (isMounted && open && !initialStylesSet) {
            updatePopoverPosition();
        }
        window.addEventListener('resize', updatePopoverPosition);
        const observer = new ResizeObserver(updatePopoverPosition);
        if (popover) {
            observer.observe(popover);
        }
        return () => {
            window.removeEventListener('resize', updatePopoverPosition);
            if (popover) {
                observer.unobserve(popover);
            }
        };
    }, [initialStylesSet, isMounted, open, updatePopoverPosition]);
    // Update popover position on scroll
    useEffect(() => {
        if (popoverRef.current && isMounted && open) {
            const scrollableElements = [];
            let currentElement = refElement;
            // Traverse up the DOM tree and collect all scrollable parent elements
            while (currentElement) {
                const { overflowY } = window.getComputedStyle(currentElement);
                if (overflowY === 'scroll' || overflowY === 'auto') {
                    scrollableElements.push(currentElement);
                }
                currentElement = currentElement.parentElement;
            }
            // Add the window as a scrollable element
            scrollableElements.push(window);
            const handleScroll = () => {
                updatePopoverPosition();
            };
            // Add scroll event listeners to all scrollable elements
            scrollableElements.forEach((element) => {
                element.addEventListener('scroll', handleScroll);
            });
            return () => {
                // Remove scroll event listeners from all scrollable elements
                scrollableElements.forEach((element) => {
                    element.removeEventListener('scroll', handleScroll);
                });
            };
        }
    }, [isMounted, open, refElement, updatePopoverPosition]);
    // Set isMounted to true on mount and to false on unmount
    useEffect(() => {
        updateState({ isMounted: true });
        return () => {
            updateState({ isMounted: false });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // Close popover on click outside of popover and refElement or on escape key press
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMounted && closeOnOutsideClick && open) {
                const target = event.target;
                /**
                 * Check if the clicked element is inside any MUI popover.
                 * This is a little brittle. Hopefully will deprecate all MUI components
                 * at some point.
                 */
                const isInsideAnyMUIpopover = target.closest('.MuiPopover-root');
                // Check if the clicked element is inside the current popover
                let isInsideCurrentPopover = false;
                let currentElement = target;
                // Check if target is inside the current popover
                while (currentElement) {
                    if (currentElement === popoverRef.current) {
                        isInsideCurrentPopover = true;
                        break;
                    }
                    currentElement = currentElement.parentElement;
                }
                if (popoverRef.current &&
                    !popoverRef.current.contains(target) &&
                    refElement &&
                    !refElement.contains(target) &&
                    !isInsideAnyMUIpopover &&
                    !isInsideCurrentPopover) {
                    onClose();
                }
            }
        };
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [closeOnOutsideClick, isMounted, onClose, open, refElement]);
    const [rootClass] = getClassName({
        className,
        rootClass: 'onsolveUclPopover',
        modifiers: {
            isVisible: open && initialStylesSet,
        },
        styles,
    });
    return isMounted && open
        ? ReactDOM.createPortal(<div className={rootClass} ref={popoverRef} style={{ ...popoverStyles, overflow: noScroll ? 'hidden' : 'auto' }}>
                  {children}
              </div>, popoverRoot)
        : null;
};
